import { useEffect, useState, useCallback, useMemo } from 'react';
import { getUserLocation } from '../lib/api';

// Default when we have no signal yet (before location or override resolves).
const DEFAULT_LOCATION = { country: 'US', currency: 'USD', symbol: '$', rate: 0.012, decimals: 2 };

// All currencies that the backend knows about (keep in sync with server EXCHANGE_RATES).
export const SUPPORTED_CURRENCIES = [
  { code: 'USD', label: 'US Dollar',     symbol: '$',    rate: 0.012,  decimals: 2 },
  { code: 'INR', label: 'Indian Rupee',  symbol: '₹',    rate: 1.0,    decimals: 0 },
  { code: 'EUR', label: 'Euro',          symbol: '€',    rate: 0.011,  decimals: 2 },
  { code: 'GBP', label: 'British Pound', symbol: '£',    rate: 0.0095, decimals: 2 },
  { code: 'CAD', label: 'Canadian $',    symbol: 'C$',   rate: 0.016,  decimals: 2 },
  { code: 'AUD', label: 'Australian $',  symbol: 'A$',   rate: 0.018,  decimals: 2 },
  { code: 'AED', label: 'UAE Dirham',    symbol: 'د.إ ', rate: 0.044,  decimals: 2 },
  { code: 'SGD', label: 'Singapore $',   symbol: 'S$',   rate: 0.016,  decimals: 2 },
  { code: 'JPY', label: 'Japanese Yen',  symbol: '¥',    rate: 1.8,    decimals: 0 },
  { code: 'CNY', label: 'Chinese Yuan',  symbol: '¥',    rate: 0.087,  decimals: 2 },
  { code: 'MYR', label: 'Malaysian RM',  symbol: 'RM ',  rate: 0.055,  decimals: 2 },
];

const OVERRIDE_KEY = 'today_currency_override';     // 'AUTO' | currency code
const CACHE_KEY = 'today_location';
const EVT = 'today:currency-change';

function buildOverrideLocation(code) {
  const c = SUPPORTED_CURRENCIES.find(x => x.code === code);
  if (!c) return null;
  return { country: 'MANUAL', currency: c.code, symbol: c.symbol, rate: c.rate, decimals: c.decimals };
}

function readOverride() {
  const v = localStorage.getItem(OVERRIDE_KEY);
  if (!v || v === 'AUTO') return null;
  return buildOverrideLocation(v);
}

// Module-level auto-detect cache (one network call per browser session).
let autoDetected = null;
let inflight = null;

async function fetchAutoDetected() {
  if (autoDetected) return autoDetected;
  if (inflight) return inflight;

  const saved = localStorage.getItem(CACHE_KEY);
  if (saved) {
    try { autoDetected = JSON.parse(saved); return autoDetected; } catch {}
  }
  inflight = (async () => {
    try {
      const res = await getUserLocation();
      autoDetected = res.data;
      localStorage.setItem(CACHE_KEY, JSON.stringify(autoDetected));
    } catch {
      autoDetected = DEFAULT_LOCATION;
    } finally {
      inflight = null;
    }
    return autoDetected;
  })();
  return inflight;
}

function currentLocation() {
  return readOverride() || autoDetected || DEFAULT_LOCATION;
}

export function setCurrencyOverride(codeOrAuto) {
  if (!codeOrAuto || codeOrAuto === 'AUTO') {
    localStorage.removeItem(OVERRIDE_KEY);
  } else {
    localStorage.setItem(OVERRIDE_KEY, codeOrAuto);
  }
  window.dispatchEvent(new CustomEvent(EVT));
}

export function getCurrencyOverride() {
  return localStorage.getItem(OVERRIDE_KEY) || 'AUTO';
}

function formatAmount(inr, loc) {
  if (!loc || !loc.rate) return `$${(inr * 0.012).toFixed(2)}`;
  const converted = inr * loc.rate;
  const display = loc.decimals === 0 ? `${Math.round(converted)}` : converted.toFixed(loc.decimals);
  return `${loc.symbol}${display}`;
}

export default function useCurrency() {
  const [loc, setLoc] = useState(currentLocation);

  useEffect(() => {
    // Kick off auto-detect if we don't have it (and no manual override).
    if (!readOverride() && !autoDetected) {
      fetchAutoDetected().then(() => setLoc(currentLocation()));
    }
    const handler = () => setLoc(currentLocation());
    window.addEventListener(EVT, handler);
    return () => window.removeEventListener(EVT, handler);
  }, []);

  const format = useCallback((inr) => formatAmount(inr, loc), [loc]);

  return useMemo(() => ({
    loading: !loc,
    currency: loc?.currency || DEFAULT_LOCATION.currency,
    symbol: loc?.symbol || DEFAULT_LOCATION.symbol,
    country: loc?.country || DEFAULT_LOCATION.country,
    isINR: (loc?.currency || DEFAULT_LOCATION.currency) === 'INR',
    isManualOverride: (loc?.country === 'MANUAL'),
    format,
  }), [loc, format]);
}
